import MDButton from "components/MDButton";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import GetSurveyReport from "hooks/SurveyReport/GetSurveyReport";
import DataTable from "comps/Tables/DataTable";
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment/moment";
import MDBox from "components/MDBox";
import SearchInput from "components/searchInput";
import Union2 from "assets/images/Union2.png";
import csvDownload from "json-to-csv-export";
import { InfoOutlined } from "@mui/icons-material";
import TLinkApi from "services/tlink.api";
import { enqueueSnackbar } from "notistack";
import { set } from "react-hook-form";

function SurveyReport() {
  let navigate = useNavigate();
  let { id } = useParams();
  const { surveyReport, isLoading, setIsLoading } = GetSurveyReport({ id });
  const [page, setPage] = useState(1);
  const [surveyReports, setSurveyReports] = useState([]);
  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [anchorEl, setAnchorEl] = useState(null);
  const [data, setData] = useState({});
  const [token, setToken] = useState();
  const handleOpen = (event, token) => {
    getData(token);
    setAnchorEl(event.currentTarget);
    setToken(token);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setData({});
    setToken(null);
  };

  const open = token;

  const getData = async (token) => {
    try {
      setIsLoading(true);
      const data = await TLinkApi.get(`/survey/token/${token}`);
      setData(data.data);
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar("Failed to fetch url details", { variant: "error" });
      setIsLoading(false);
    }
  };

  let rows = surveyReports.slice(
    page * entriesPerPage - entriesPerPage,
    page * entriesPerPage,
  );

  useEffect(() => {
    setSurveyReports(surveyReport);
    // eslint-disable-next-line
  }, [surveyReport]);

  useEffect(() => {
    if (search && search !== "") {
      const fSurveyReports = surveyReports
        .filter((value) => {
          if (search === "") return true;
          else {
            const s = search.toLowerCase();
            let match = false;
            if (value.status && value.status.toLowerCase().includes(s)) {
              match = true;
            }
            if (
              value.vendorName &&
              value.vendorName.toLowerCase().includes(s)
            ) {
              match = true;
            }
            if (value.token && value.token.toLowerCase().includes(s)) {
              match = true;
            }
            if (value.subStatus && value.subStatus.toLowerCase().includes(s)) {
              match = true;
            }
            return match;
          }
        })
        .map((c) => c);
      setSurveyReports(fSurveyReports);
    } else {
      setSurveyReports(surveyReport);
    }
    // eslint-disable-next-line
  }, [search]);

  let columns = [
    {
      accessor: "surveyId",
      Header: "Survey Id",
      align: "left",
    },
    {
      accessor: "status",
      Header: "Status",
      sortable: true,
      align: "left",
    },

    {
      accessor: "subStatus",
      Header: "Sub Status",
      sortable: true,
      align: "left",
    },

    {
      accessor: "vendorName",
      Header: "Supplier Name",
      sortable: true,
      align: "left",
    },
    {
      accessor: "supplierToken",
      Header: "Supplier Token",
      align: "left",
      sortable: true,
    },
    {
      accessor: "token",
      Header: "Token",
      align: "left",
      sortable: true,
      Cell: ({ row }) => {
        const name = row.original.token;
        const truncatedName =
          name?.length > 15 ? name.substring(0, 15) + "..." : name;
        return (
          <Tooltip title={name}>
            <div>{truncatedName}</div>
          </Tooltip>
        );
      },
    },

    {
      accessor: "startDate",
      Header: "Start Date",
      align: "center",
      sortable: true,
      headerAlign: "center",
      Cell: (params) =>
        `${moment(params.row.original.startDate).format("DD-MM-YYYY")}`,
    },
    {
      accessor: "endDate",
      Header: "End Date",
      align: "center",
      sortable: true,
      headerAlign: "center",
      Cell: (params) =>
        `${moment(params.row.original.endDate).format("DD-MM-YYYY")}`,
    },
    {
      accessor: "token",
      Header: "Actions",
      align: "center",
      sortable: true,
      headerAlign: "center",
      Cell: ({ row }) => (
        <>
          <IconButton
            size="small"
            onClick={(event) => handleOpen(event, row.original.token)}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>

          <Popover
            open={open === row.original.token}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <Box sx={{ p: 2, width: "420px" }}>
              {Object.entries(data).map(([key, value]) => (
                <Box key={key} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {key}
                    <Typography
                      variant="subtitle2"
                      style={{ wordBreak: "break-all" }}
                    >
                      {value}
                    </Typography>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Popover>
        </>
      ),
    },
  ];

  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox display="flex" justifyContent="center">
        <MDBox py={2} mt={3} borderRadius="5px" bgColor="white" width="98%">
          <Grid container spacing={2} pb={3}>
            <Grid
              px={2}
              item
              xs={12}
              md={12}
              display="flex"
              justifyContent="flex-end"
              flexDirection="row"
              sx={{
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-end", md: "center" },
              }}
            >
              <SearchInput setSearch={setSearch} search={search} />
            </Grid>
          </Grid>
          <DataTable
            table={{ columns, rows }}
            isSorted
            noEndBorder
            entriesPerPage={entriesPerPage}
            canSearch={false}
            tablePage={page}
            setTablePage={setPage}
            tableMaxHeight={"calc(100vh - 160px)"}
            setEntriesPerPage={setEntriesPerPage}
            totalLength={Math.ceil(surveyReports.length)}
            pagination={true}
          />
        </MDBox>
      </MDBox>
      <MDBox
        mx={2}
        mt={2}
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        flexDirection="row"
        gap="10px"
      >
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => {
            const dataToConvert = {
              data: surveyReport,
              filename: "Survey Report",
              delimiter: ",",
            };
            csvDownload(dataToConvert);
          }}
        >
          <img
            src={Union2}
            width="15px"
            height="15px"
            style={{ paddingRight: "5px" }}
            alt="export"
          />
          Export
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => {
            navigate("/surveys");
          }}
        >
          Back
        </MDButton>
      </MDBox>
    </MDBox>
  );
}

export default SurveyReport;
